import { Button } from "@portal/BaseComponents/Button/AntButton";
import { CheckBoxGroup } from "@portal/BaseComponents/CheckBox/AntCheckBox";
import {
  OptionType,
  onCheckBoxGroupChange,
} from "@portal/BaseComponents/CheckBox/type";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { CheckBoxLoader } from "@portal/BaseComponents/Loader/CheckBoxLoader";
import { filterUnassigned } from "@portal/util/predicates";
import { Divider, Empty } from "antd";
import produce from "immer";
import { Fragment, useMemo, useState } from "react";
import { FilterFooter } from "../../Footer/FilterFooter";

export interface MultiSelectProps {
  initialValue?: OptionType["value"][];
  options: OptionType[];
  enableSelectAll?: boolean;
  shouldIncludeUnassigned?: boolean;
  emptyPlaceholder?: string;
  onSubmit: (value: OptionType["value"][], unassigned?: boolean) => void;
  onCancel: () => void;
  loading?: boolean;
  onCheckAllChange?: (value: boolean) => void;
  onChange?: (...args: onCheckBoxGroupChange) => void;
}

const t = {
  unassigned: "Unassigned",
  select_all: "Select All",
  none: "None",
};

const unassigned: OptionType = {
  label: "Unassigned",
  value: "-1",
};
function MultiSelect(props: MultiSelectProps) {
  const {
    initialValue,
    options,
    enableSelectAll = false,
    shouldIncludeUnassigned = false,
    emptyPlaceholder = "No Data",
    onSubmit,
    onCancel,
    loading = false,
    onCheckAllChange,
    onChange,
  } = props;

  const [selectedValues, setSelectedValues] = useState<
    OptionType["value"][] | undefined
  >(initialValue);

  const [, setCheckAll] = useState(false);

  function handleChange(...args: onCheckBoxGroupChange) {
    const [value] = args;
    setSelectedValues(value);
    setCheckAll(value.length === modifiedOptions.length);
    onCheckAllChange?.(value.length === modifiedOptions.length);
    onChange?.(...args);
  }
  function getOptions() {
    return shouldIncludeUnassigned
      ? produce(options, (draft) => {
          return [unassigned, ...draft];
        })
      : options;
  }

  function handleSubmit() {
    onSubmit?.(
      selectedValues?.filter(filterUnassigned) ?? [],
      selectedValues?.includes("-1")
    );
  }

  const modifiedOptions = useMemo(getOptions, [
    options,
    shouldIncludeUnassigned,
  ]);

  function handleSelectAll() {
    setCheckAll(true);
    setSelectedValues(modifiedOptions.map((f) => f.value));
    onCheckAllChange?.(true);
  }

  function handleSelectNone() {
    setCheckAll(false);
    setSelectedValues([]);
    onCheckAllChange?.(false);
  }
  const NoData = (
    <Empty
      description={emptyPlaceholder}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  );

  const SelectAll = (
    <Flex align="center" space="2">
      <Button
        type="text"
        size="small"
        style={{ padding: 0 }}
        onClick={handleSelectAll}
      >
        {t.select_all}
      </Button>
      <Divider type="vertical" />
      <Button
        type="text"
        size="small"
        style={{ padding: 0 }}
        onClick={handleSelectNone}
      >
        {t.none}
      </Button>
    </Flex>
  );

  const Item = (
    <CheckBoxGroup
      options={modifiedOptions}
      design="border-design"
      layout="column"
      value={selectedValues}
      onChange={handleChange}
      defaultValue={initialValue}
      style={{
        maxHeight: "160px",
        overflow: "auto",
        width: "100%",
      }}
      scroll="mac"
    />
  );

  const Footer = (
    <FilterFooter
      onCancel={onCancel}
      onSubmit={handleSubmit}
      disable={!selectedValues?.length}
    />
  );

  const Content = (
    <Flex vertical style={{ minWidth: "240px" }}>
      {enableSelectAll ? SelectAll : null}
      {Item}
      {Footer}
    </Flex>
  );

  const SelectContent = modifiedOptions.length ? Content : NoData;
  return <Fragment>{loading ? <CheckBoxLoader /> : SelectContent}</Fragment>;
}
export { MultiSelect };
